import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import KibabMenu from "../../../../assets/icons/kibab-menu";

const MENU_ITEM_HEIGHT = 35;
const MENU_PADDING = 16;
const MENU_GAP = 4;

/**
 * Kebab menu for a tray row — same look as the approval flow list's menu.
 *
 * The menu is portalled to <body> with fixed positioning: rows in the virtual
 * list are absolutely positioned inside a scrolling box, so an in-row popover
 * would be cut off (or pad the scroll area) on the last rows. It opens upward
 * when there isn't room below, and closes on outside click, scroll or resize.
 *
 * `items` is [{ label, icon, onClick }]; nothing renders when it is empty.
 */
const TrayRowActions = ({ items }) => {
  const [position, setPosition] = useState(null);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!position) return undefined;
    const close = () => setPosition(null);
    const closeOnOutsideClick = (event) => {
      if (menuRef.current?.contains(event.target)) return;
      if (buttonRef.current?.contains(event.target)) return;
      close();
    };
    document.addEventListener("mousedown", closeOnOutsideClick);
    document.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      document.removeEventListener("mousedown", closeOnOutsideClick);
      document.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [position]);

  if (!items.length) return null;

  const toggle = () => {
    if (position) {
      setPosition(null);
      return;
    }
    const rect = buttonRef.current.getBoundingClientRect();
    const menuHeight = items.length * MENU_ITEM_HEIGHT + MENU_PADDING;
    const openUp = window.innerHeight - rect.bottom < menuHeight + MENU_GAP;
    setPosition({
      right: window.innerWidth - rect.right,
      ...(openUp
        ? { bottom: window.innerHeight - rect.top + MENU_GAP }
        : { top: rect.bottom + MENU_GAP }),
    });
  };

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        title="More"
        className="flex items-center hover:opacity-70"
        onClick={toggle}
      >
        <KibabMenu />
      </button>
      {position &&
        createPortal(
          <div
            ref={menuRef}
            style={{
              position: "fixed",
              ...position,
              minWidth: "11rem",
              zIndex: 999,
              boxShadow: "0px 1px 3px 0px var(--popover-shadow-color)",
            }}
            className="border border-[var(--popover-border-color)] py-2 px-2 bg-[var(--popover-bg-color)] rounded flex flex-col"
          >
            {items.map((item) => (
              <button
                key={item.label}
                type="button"
                onClick={() => {
                  setPosition(null);
                  item.onClick();
                }}
                className="flex items-center gap-2 text-[length:--popover-font-size] font-[var(--popover-font-weight)] text-[var(--popover-fg-color)] whitespace-nowrap cursor-pointer h-[35px] hover:bg-[var(--popover-hover-color)] rounded px-[8px]"
              >
                {item.icon}
                {item.label}
              </button>
            ))}
          </div>,
          document.body
        )}
    </>
  );
};

export default TrayRowActions;
